import axios, {
  AxiosInstance,
  AxiosResponse,
  RawAxiosRequestConfig,
} from 'axios';
import { toast } from 'react-toastify';

interface ICustomError {
  message: string;
  data: any;
  status: any;
  response: {
    status: number;
    data: {
      ErrorMessage: string;
      status: boolean;
    };
  };
  config: ICustomRawAxiosRequestConfig;
}

interface ICustomRawAxiosRequestConfig extends RawAxiosRequestConfig {
  _retry?: boolean;
}

abstract class Http {
  private axiosInstance: AxiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    timeout: 180000,
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  protected httpService: <TReqData, TResData>(
    requestConfig: RawAxiosRequestConfig<TReqData>,
    isNotAuthNeededException?: boolean,
  ) => Promise<TResData>;

  protected constructor() {
    this.httpService = (
      requestConfig: RawAxiosRequestConfig,
      isAuthNeeded = true,
    ) => {
      const sessionId = localStorage.getItem('sessionId');
      if (isAuthNeeded) {
        this.axiosInstance.defaults.headers.common = {
          session: sessionId,
        };
      } else {
        delete this.axiosInstance.defaults.headers.common['session'];
      }
      return this.axiosInstance(requestConfig);
    };

    this.requestInterceptors();
    this.responseInterceptors();
  }

  private responseInterceptors() {
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: ICustomError) => {
        // Handle 401 Unauthorized errors
        if (error?.response?.status === 401) {
          // Clear session data
          localStorage.removeItem('sessionId');
          localStorage.removeItem('userId');

          // Dispatch custom event to notify store
          window.dispatchEvent(new Event('authStateCleared'));

          // Show error message
          toast.error('Session expired. Please login again.');

          // Redirect to login page
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Handle other errors
        if (error?.response?.data?.ErrorMessage) {
          toast.error(String(error?.response?.data?.ErrorMessage));
        } else if (
          error?.response?.status >= 500 &&
          error?.response?.status < 600
        ) {
          const { ErrorMessage } = error.response.data;
          toast.error(String(ErrorMessage));
        } else {
          toast.error('Error Occurred on Server.');
        }
        return Promise.reject(error);
      },
    );
  }

  private requestInterceptors() {
    this.axiosInstance.interceptors.request.use(
      (config) => config,
      (error) => {
        toast.error('No Response');
        return Promise.reject(error);
      },
    );
  }
}

export default Http;
